const TURNSTILE_SCRIPT_URL =
  'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit'
const TURNSTILE_SITE_KEY = '0x4AAAAAAAM4kc1fihSt7dUz'
const TURNSTILE_TIMEOUT_MS = 30_000

type TurnstileApi = Readonly<{
  execute: (widgetId: string) => void
  remove: (widgetId: string) => void
  render: (
    container: HTMLElement,
    options: Readonly<{
      sitekey: string
      execution: 'execute'
      appearance: 'interaction-only'
      callback: (token: string) => void
      'error-callback': () => void
      'timeout-callback': () => void
    }>,
  ) => string
}>

declare global {
  interface Window {
    turnstile?: TurnstileApi
  }
}

let turnstileScriptPromise: Promise<TurnstileApi> | undefined
let turnstileQueue: Promise<void> = Promise.resolve()

function abortError(): DOMException {
  return new DOMException('The image upload was cancelled', 'AbortError')
}

function loadTurnstile(): Promise<TurnstileApi> {
  if (window.turnstile) return Promise.resolve(window.turnstile)
  if (turnstileScriptPromise) return turnstileScriptPromise

  const script =
    document.querySelector<HTMLScriptElement>(
      `script[src="${TURNSTILE_SCRIPT_URL}"]`,
    ) ?? document.createElement('script')
  const isNewScript = !script.isConnected

  turnstileScriptPromise = new Promise<TurnstileApi>((resolve, reject) => {
    const cleanup = (): void => {
      window.clearTimeout(timeoutId)
      script.removeEventListener('load', finish)
      script.removeEventListener('error', fail)
    }
    const fail = (): void => {
      cleanup()
      script.remove()
      reject(new Error('The upload security check did not load'))
    }
    const finish = (): void => {
      if (!window.turnstile) {
        fail()
        return
      }

      cleanup()
      resolve(window.turnstile)
    }
    const timeoutId = window.setTimeout(fail, TURNSTILE_TIMEOUT_MS)

    script.addEventListener('load', finish, { once: true })
    script.addEventListener('error', fail, { once: true })

    if (isNewScript) {
      script.src = TURNSTILE_SCRIPT_URL
      script.async = true
      script.defer = true
      document.head.append(script)
    }
  }).catch((error: unknown) => {
    turnstileScriptPromise = undefined
    throw error
  })

  return turnstileScriptPromise
}

async function requestTurnstileToken(signal?: AbortSignal): Promise<string> {
  if (signal?.aborted) throw abortError()

  const turnstile = await loadTurnstile()
  if (signal?.aborted) throw abortError()

  return new Promise<string>((resolve, reject) => {
    const container = document.createElement('div')
    document.body.append(container)
    let widgetId: string | undefined

    const cleanup = (): void => {
      window.clearTimeout(timeoutId)
      signal?.removeEventListener('abort', cancel)
      if (widgetId) turnstile.remove(widgetId)
      container.remove()
    }
    const complete = (token: string): void => {
      cleanup()
      resolve(token)
    }
    const fail = (): void => {
      cleanup()
      reject(new Error('The upload security check failed'))
    }
    const cancel = (): void => {
      cleanup()
      reject(abortError())
    }
    const timeoutId = window.setTimeout(fail, TURNSTILE_TIMEOUT_MS)

    signal?.addEventListener('abort', cancel, { once: true })
    try {
      widgetId = turnstile.render(container, {
        sitekey: TURNSTILE_SITE_KEY,
        execution: 'execute',
        appearance: 'interaction-only',
        callback: complete,
        'error-callback': fail,
        'timeout-callback': fail,
      })
      turnstile.execute(widgetId)
    } catch {
      fail()
    }
  })
}

export function getGuestMediaTurnstileToken(
  signal?: AbortSignal,
): Promise<string> {
  const request = turnstileQueue.then(() => requestTurnstileToken(signal))
  turnstileQueue = request.then(
    () => undefined,
    () => undefined,
  )
  return request
}
